import { useState, useEffect, useCallback } from "react";
import { usePlaidLink } from "react-plaid-link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Building2, 
  Plus, 
  CreditCard,
  Link2,
  RefreshCw,
  Trash2,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  ArrowDownLeft,
  ArrowUpRight,
  Landmark,
  ExternalLink,
  Loader2,
  Upload,
  FileSpreadsheet
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DashboardLayout from "@/components/DashboardLayout";
import BankStatementsTab from "./BankStatementsTab";
import BatchAnalysisTab from "./BatchAnalysisTab";

import { API_URL } from "@/utils/api";

// Plaid Link Component
function PlaidLinkButton({ onSuccess, disabled }) {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plaidEnv, setPlaidEnv] = useState(null);

  const fetchLinkToken = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      // Pass redirect_uri for OAuth flow in production
      const redirectUri = `${window.location.origin}/oauth-callback`;
      
      const response = await fetch(`${API_URL}/api/plaid/link-token`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ redirect_uri: redirectUri })
      });
      
      if (response.ok) {
        const data = await response.json();
        setLinkToken(data.link_token);
        setPlaidEnv(data.plaid_env);
        console.log("Plaid Link initialized:", { env: data.plaid_env, redirect_uri: data.redirect_uri });
      } else {
        const errorData = await response.json();
        // Check if it's an OAuth redirect URI configuration error
        if (errorData.detail && errorData.detail.includes("OAuth redirect URI must be configured")) {
          toast.error(
            "OAuth setup required: Please add the redirect URI to your Plaid Dashboard (see instructions below)",
            { duration: 8000 }
          );
        } else {
          toast.error(errorData.detail || "Failed to initialize Plaid Link");
        }
      }
    } catch (err) {
      console.error("Error fetching link token:", err);
      toast.error("Failed to connect to Plaid");
    } finally {
      setLoading(false);
    }
  };

  const onPlaidSuccess = useCallback(async (public_token, metadata) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/plaid/exchange-token`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ 
          public_token,
          institution_id: metadata.institution?.institution_id,
          institution_name: metadata.institution?.name
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${metadata.institution?.name || "Bank"} connected successfully!`);
        onSuccess && onSuccess(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to connect bank");
      }
    } catch (err) {
      console.error("Error exchanging token:", err);
      toast.error("Failed to complete bank connection");
    }
  }, [onSuccess]);

  const config = {
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: (err) => {
      if (err) {
        console.error("Plaid Link error:", err);
      }
      setLinkToken(null);
    }
  };

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready, open]);

  return (
    <Button 
      className="bg-[#2563eb] hover:bg-[#1d4ed8] gap-2" 
      onClick={fetchLinkToken}
      disabled={disabled || loading}
      data-testid="connect-bank-btn"
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Initializing...
        </>
      ) : (
        <>
          <Plus className="w-4 h-4" />
          Connect Bank
        </>
      )}
    </Button>
  );
}

export default function BankConnectionsPage({ user }) {
  const [connections, setConnections] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [failedLogos, setFailedLogos] = useState(new Set());

  useEffect(() => {
    fetchConnections();
    fetchTransactions();
  }, []);

  const fetchConnections = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/plaid/connections`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        // Flatten accounts into connection display items
        const flattenedConnections = [];
        for (const conn of data) {
          if (conn.accounts && conn.accounts.length > 0) {
            for (const acc of conn.accounts) {
              flattenedConnections.push({
                connection_id: conn.connection_id,
                institution_name: conn.institution_name,
                institution_logo: conn.institution_logo,
                account_name: acc.name || acc.official_name,
                mask: acc.mask,
                current_balance: acc.current_balance || 0,
                available_balance: acc.available_balance || 0,
                account_type: acc.type,
                last_synced: conn.last_sync,
                status: conn.is_active ? "active" : "inactive",
                needs_reauth: conn.needs_reauth || false,
                error_message: conn.error_message
              });
            }
          } else {
            flattenedConnections.push({
              connection_id: conn.connection_id,
              institution_name: conn.institution_name,
              institution_logo: conn.institution_logo,
              account_name: "Connected Account",
              mask: "****",
              current_balance: 0,
              available_balance: 0,
              last_synced: conn.last_sync,
              status: conn.is_active ? "active" : "inactive",
              needs_reauth: conn.needs_reauth || false,
              error_message: conn.error_message
            });
          }
        }
        setConnections(flattenedConnections);
      }
    } catch (err) {
      console.error("Error fetching connections:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/plaid/transactions`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
    }
  };

  const handleBankConnected = () => {
    fetchConnections();
    fetchTransactions();
  };

  const handleSyncConnection = async (connection) => {
    setSyncing(connection.connection_id);

    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/plaid/sync/${connection.connection_id}`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Synced ${data.added || 0} new transactions`);
        fetchConnections();
        fetchTransactions();
      } else {
        toast.error("Failed to sync account");
      }
    } catch (err) {
      toast.error("Sync error: " + err.message);
    } finally {
      setSyncing(null);
    }
  };

  const handleDeleteConnection = async () => {
    if (!selectedConnection) return;

    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/plaid/connections/${selectedConnection.connection_id}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        toast.success("Bank account disconnected");
        fetchConnections();
        fetchTransactions();
      } else {
        toast.error("Failed to disconnect account");
      }
    } catch (err) {
      toast.error("Error: " + err.message);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedConnection(null);
    }
  };

  // Re-authenticate a bank connection (update mode)
  const handleReauthenticate = async (connection) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/plaid/update-mode/${connection.connection_id}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Open Plaid Link in update mode
        setLinkToken(data.link_token);
        // The usePlaidLink hook will handle opening
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start re-authentication");
      }
    } catch (err) {
      toast.error("Error: " + err.message);
    }
  };

  const totalBalance = connections.reduce((sum, c) => sum + (c.current_balance || 0), 0);
  const totalAvailable = connections.reduce((sum, c) => sum + (c.available_balance || 0), 0);
  const needsReauthCount = connections.filter(c => c.needs_reauth).length;

  return (
    <DashboardLayout user={user}>
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Bank Connections</h1>
            <p className="text-sm text-gray-500 mt-1">Connect your bank accounts or upload statements</p>
          </div>
        </div>

        <Tabs defaultValue="statements" className="w-full">
          <div className="overflow-x-auto -mx-3 px-3 pb-2">
            <TabsList className="inline-flex min-w-max bg-[#F7FFE5] rounded-xl p-1 gap-1">
              <TabsTrigger value="statements" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs md:text-sm px-2 md:px-4 whitespace-nowrap">
                <FileSpreadsheet className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                Statement Upload
              </TabsTrigger>
              <TabsTrigger value="analysis" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs md:text-sm px-2 md:px-4 whitespace-nowrap">
                <TrendingUp className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                Batch Analysis
              </TabsTrigger>
              <TabsTrigger value="plaid" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs md:text-sm px-2 md:px-4 whitespace-nowrap">
                <Landmark className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                Connect Bank
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="statements" className="mt-6">
            <BankStatementsTab />
          </TabsContent>

          <TabsContent value="analysis" className="mt-6">
            <BatchAnalysisTab />
          </TabsContent>

          <TabsContent value="plaid" className="mt-4 md:mt-6">
            <div className="space-y-4 md:space-y-6">
              <div className="flex justify-end">
                <PlaidLinkButton onSuccess={handleBankConnected} />
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <Card className="border border-gray-200">
                  <CardContent className="p-3 md:pt-4 md:pb-4">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Landmark className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-lg md:text-2xl font-bold text-gray-900">{connections.length}</p>
                        <p className="text-xs text-gray-500 truncate">Connected</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-gray-200">
                  <CardContent className="p-3 md:pt-4 md:pb-4">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-lg md:text-2xl font-bold text-gray-900">${totalBalance.toLocaleString()}</p>
                        <p className="text-xs text-gray-500 truncate">Balance</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-gray-200">
                  <CardContent className="p-3 md:pt-4 md:pb-4">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="w-8 h-8 md:w-10 md:h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-lg md:text-2xl font-bold text-gray-900">${totalAvailable.toLocaleString()}</p>
                        <p className="text-xs text-gray-500 truncate">Available</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-gray-200">
                  <CardContent className="p-3 md:pt-4 md:pb-4">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="w-8 h-8 md:w-10 md:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CreditCard className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-lg md:text-2xl font-bold text-gray-900">{transactions.length}</p>
                        <p className="text-xs text-gray-500 truncate">Transactions</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

        {/* Connected Accounts */}
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg">Connected Accounts</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
              </div>
            ) : connections.length === 0 ? (
              <div className="text-center py-12">
                <Landmark className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">No bank accounts connected</p>
                <p className="text-sm text-gray-400 mb-4">
                  Connect your bank to automatically sync transactions and track your finances.
                </p>
                <PlaidLinkButton onSuccess={handleBankConnected} />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Re-auth Warning Banner */}
                {needsReauthCount > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-900">
                        {needsReauthCount} bank connection{needsReauthCount > 1 ? 's need' : ' needs'} re-authentication
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        Please re-authenticate to continue syncing transactions. This is required periodically for security.
                      </p>
                    </div>
                  </div>
                )}
                
                {connections.map((connection) => (
                  <div 
                    key={connection.connection_id} 
                    className={`flex items-center justify-between p-4 rounded-xl transition-colors ${
                      connection.needs_reauth 
                        ? 'bg-amber-50 border border-amber-200' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    data-testid={`connection-${connection.connection_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center border border-gray-200 overflow-hidden">
                        {connection.institution_logo && !failedLogos.has(connection.connection_id) ? (
                          <img 
                            src={connection.institution_logo} 
                            alt={connection.institution_name}
                            className="w-8 h-8 object-contain"
                            onError={() => setFailedLogos(prev => new Set([...prev, connection.connection_id]))}
                          />
                        ) : (
                          <Building2 className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{connection.institution_name}</p>
                        <p className="text-sm text-gray-500">
                          {connection.account_name} •••• {connection.mask}
                        </p>
                        {connection.needs_reauth ? (
                          <p className="text-xs text-amber-600 font-medium">
                            <AlertTriangle className="w-3 h-3 inline mr-1" />
                            {connection.error_message || "Re-authentication required"}
                          </p>
                        ) : (
                          <p className="text-xs text-gray-400">
                            Last synced: {connection.last_synced ? new Date(connection.last_synced).toLocaleString() : "Never"}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          ${(connection.current_balance || 0).toLocaleString()}
                        </p>
                        {connection.needs_reauth ? (
                          <Badge className="bg-amber-100 text-amber-700">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Needs Re-auth
                          </Badge>
                        ) : (
                          <Badge className={connection.status === "active" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {connection.status || "Active"}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {connection.needs_reauth ? (
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => handleReauthenticate(connection)}
                            className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg"
                            data-testid={`reauth-btn-${connection.connection_id}`}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Re-authenticate
                          </Button>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSyncConnection(connection)}
                            disabled={syncing === connection.connection_id}
                            className="rounded-lg"
                            data-testid={`sync-btn-${connection.connection_id}`}
                          >
                            {syncing === connection.connection_id ? (
                              <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Syncing...</>
                            ) : (
                              <><RefreshCw className="w-3 h-3 mr-1" /> Sync</>
                            )}
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                          onClick={() => {
                            setSelectedConnection(connection);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`disconnect-${connection.connection_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        {transactions.length > 0 && (
          <Card className="border border-gray-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Recent Transactions</CardTitle>
              <Badge variant="outline">{transactions.length} total</Badge>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Date</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Description</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Category</th>
                      <th className="text-right text-xs font-medium text-gray-500 uppercase px-4 py-3">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {transactions.slice(0, 10).map((txn, idx) => (
                      <tr key={txn.transaction_id || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {new Date(txn.date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {txn.amount < 0 ? (
                              <ArrowUpRight className="w-4 h-4 text-red-500" />
                            ) : (
                              <ArrowDownLeft className="w-4 h-4 text-green-500" />
                            )}
                            <span className="text-sm font-medium text-gray-900">{txn.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-xs">
                            {txn.category?.[0] || "Uncategorized"}
                          </Badge>
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium text-right ${txn.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {txn.amount < 0 ? '-' : '+'}${Math.abs(txn.amount).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* OAuth Setup Info Card */}
        <Card className="border border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-900">Production Bank Connection Setup</h3>
                <p className="text-sm text-amber-700 mt-1">
                  For production bank connections (like Chase, Wells Fargo, Bank of America), you must add the OAuth redirect URI to your Plaid Dashboard:
                </p>
                <div className="mt-2 bg-white rounded-lg p-3 border border-amber-200">
                  <p className="text-xs text-gray-500 mb-1">Add this URI to Plaid Dashboard → Team Settings → API → Allowed redirect URIs:</p>
                  <code className="text-sm text-amber-800 font-mono bg-amber-100 px-2 py-1 rounded block break-all">
                    {window.location.origin}/oauth-callback
                  </code>
                </div>
                <a 
                  href="https://dashboard.plaid.com/team/api" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-amber-600 hover:text-amber-800 font-medium inline-flex items-center gap-1 mt-2"
                >
                  Open Plaid Dashboard <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Info Card */}
        <Card className="border border-gray-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">Secure Bank Connection</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Bank connections are powered by Plaid, a secure financial service used by millions. 
                  Your credentials are never stored on our servers. All data is encrypted end-to-end.
                </p>
                <a 
                  href="https://plaid.com/how-it-works-for-consumers/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1 mt-2"
                >
                  Learn more about Plaid security <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Bank Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disconnect {selectedConnection?.institution_name}? 
              Transaction syncing will stop for this account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConnection}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-disconnect-btn"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
